import { ErrorMessage } from '@hookform/error-message';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import WorkIcon from '@mui/icons-material/Work';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import SignUpSkeleton from './sign-up-skeleton';
import SmallForm from './small-form';

import Arranger from '~c/components/base/arranger';
import Button from '~c/components/base/button';
import { BrandLogo as BrandLogoLight } from '~c/components/base/icon/asenion-logo';
import { BrandLogo as BrandLogoDark } from '~c/components/base/icon/asenion-logo-dark';
import Link from '~c/components/base/link';
import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import TextField from '~c/components/base/text-field';
import {
  useYupValidationResolver,
  muiError,
} from '~c/hooks/yup-validation-resolver';
import { nodeAuthApi } from '~c/services';

interface InputData {
  email: string;
  password: string;
  firstname: string;
  lastname: string;
  job_title: string;
  termsAccepted: boolean;
  SignUpResult: string;
  SignUpSuccess: string;
}
const EMAIL_REGEX = /^[^@]+@[^@]+\.[^@]+$/;
const validationSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .matches(EMAIL_REGEX, 'User email is invalid!')
    .required('User email is invalid!'),
  firstname: yup
    .string()
    .trim()
    .required('First name is required')
    .test('no-html', 'First name cannot contain HTML tags', (value) => {
      if (!value) {
        return true;
      }
      return !/<[^>]*>/g.test(value);
    }),
  lastname: yup
    .string()
    .trim()
    .required('Last name is required')
    .test('no-html', 'Last name cannot contain HTML tags', (value) => {
      if (!value) {
        return true;
      }
      return !/<[^>]*>/g.test(value);
    }),
  password: yup
    .string()
    .trim()
    .validatePasswordStrength()
    .required(
      'Password must be 10+ chars with uppercase, lowercase, number & special character.'
    ),
  job_title: yup
    .string()
    .trim()
    .required('Job title is required')
    .test('no-html', 'Job title cannot contain HTML tags', (value) => {
      if (!value) {
        return true;
      }
      return !/<[^>]*>/g.test(value);
    }),
  termsAccepted: yup
    .boolean()
    .oneOf([true], 'You must accept the Privacy Policy and Terms of Use')
    .required('You must accept the Privacy Policy and Terms of Use'),
});

interface Props {
  className?: string;
}

const SignUpImpl: React.FC<Props> = ({ className }) => {
  const [createUser, { isLoading }] = nodeAuthApi.useSignupMutation();
  const navigate = useNavigate();
  const theme = useTheme();
  const resolver = useYupValidationResolver(validationSchema);
  const [emailFromQuery, setEmailFromQuery] = React.useState<string | null>(
    null
  );
  const [isAppReady, setIsAppReady] = useState<string>('');

  const [invitationToken, setInvitationToken] = React.useState<string | null>(
    null
  );
  // True once the URL query params have been read; prevents a flash of the
  // "invitation required" screen before the effect has run on first render.
  const [urlParamsParsed, setUrlParamsParsed] = React.useState(false);

  const {
    data: invitationData,
    isLoading: invitationLoading,
    error: invitationError,
  } = nodeAuthApi.useInvitationVerificationQuery(
    { token: invitationToken! },
    { skip: !invitationToken }
  );

  const {
    register,
    formState,
    setError,
    clearErrors,
    handleSubmit,
    setValue,
    watch,
  } = useForm<InputData>({
    resolver,
    mode: 'onChange',
  });
  const termsAccepted = watch('termsAccepted');
  const [showPassword, setShowPassword] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const prefersDarkMode = theme.palette.mode === 'dark';
  const { errors } = formState;

  // Read email and invitation token from URL query parameters
  React.useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const email = queryParams.get('email');
    const invitation = queryParams.get('invitation');

    if (email) {
      setEmailFromQuery(email);
      setValue('email', email);
    }

    if (invitation) {
      setInvitationToken(invitation);
    }

    setUrlParamsParsed(true);
  }, [setValue]);

  // Surface invitation verification errors in the form
  React.useEffect(() => {
    if (invitationError) {
      setError('SignUpResult', {
        message: 'Invalid or expired invitation link.',
      });
    }
  }, [invitationError, setError]);

  // Check if app is ready (JavaScript loaded)
  useEffect(() => {
    setIsAppReady(window.__ENV__.GOOGLE_CLIENT_ID);
  }, []);

  const toggleShowPassword = () => setShowPassword((show) => !show);

  const onSubmit = async (input: InputData) => {
    try {
      const response = await createUser({
        email: input.email.toLowerCase(),
        password: input.password,
        firstName: input.firstname,
        lastName: input.lastname,
        jobTitle: input.job_title,
        invitationToken: invitationToken ?? undefined,
        termsAccepted: input.termsAccepted,
      }).unwrap();

      // Use server-provided success message for consistent, secure messaging
      if (response.success && response.message) {
        setError('SignUpSuccess', {
          message: response.message,
        });
      }

      setTimeout(() => {
        navigate('/sign-in');
      }, 5000);
    } catch (error: any) {
      // Handle error from API - server provides error messages
      const errorResponse = error?.data || error;
      setError('SignUpResult', {
        message:
          errorResponse?.data?.error ??
          errorResponse?.error ??
          errorResponse?.message ??
          'An error occurred. Please try again.',
      });
    }
  };

  // After URL params are parsed, show a clear message if no invitation token
  // is present — signups require an invitation from an org admin.
  if (urlParamsParsed && !invitationToken) {
    return (
      <SmallForm
        className={className}
        title={
          <Arranger direction="column" alignItems="center">
            <div className="logo-container">
              {prefersDarkMode ? <BrandLogoLight /> : <BrandLogoDark />}
            </div>
            <Spacer mb={spc.xs} />
            <Text sz="body16" sx={{ pt: spc.xxxs }}>
              Invitation Required
            </Text>
          </Arranger>
        }
      >
        <Arranger direction="column" alignItems="center">
          <Text sz="body14" sx={{ textAlign: 'center', mb: spc.s }}>
            Signups are by invitation only. If you believe you should have
            access, please contact your organization admin.
          </Text>
          <Text sz="body12">
            {'Already have an account? '}
            <Link href="/Sign-In">Sign In</Link>
          </Text>
        </Arranger>
      </SmallForm>
    );
  }

  return (
    <>
      {isAppReady ? (
        <SmallForm
          className={className}
          title={
            <Arranger direction="column" alignItems="center">
              <div className="logo-container">
                {prefersDarkMode ? <BrandLogoLight /> : <BrandLogoDark />}
              </div>
              <Spacer mb={spc.xs} />

              <Text
                sz="body16"
                sx={{ pt: spc.xxxs }}
                style={{ display: showContent ? 'none' : 'block' }}
              >
                Join Your Team
              </Text>
            </Arranger>
          }
        >
          {/* Invitation org info */}
          {invitationData && (
            <Arranger alignItems="center" direction="column">
              <Text>
                You&apos;re invited to join{' '}
                {invitationData.invitation?.organization.name}
              </Text>
              <Spacer mb={spc.s} />
            </Arranger>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <TextField
              {...register('email', {
                onChange: () => clearErrors('SignUpResult'),
              })}
              {...muiError(errors, 'email')}
              required
              fullWidth
              style={{ display: showContent ? 'none' : 'block' }}
              label="Email Address"
              autoComplete="email"
              autoFocus={!emailFromQuery}
              sx={{ pb: spc.between_vertical_s }}
              startAdornment={<EmailIcon />}
              disabled={!!emailFromQuery}
            />
            <TextField
              {...register('password', {
                onChange: () => clearErrors('SignUpResult'),
              })}
              {...muiError(errors, 'password')}
              required
              fullWidth
              style={{ display: showContent ? 'none' : 'block' }}
              label="Password"
              sx={{ pb: spc.between_vertical_s }}
              type={showPassword ? 'text' : 'password'}
              autoComplete="off"
              startAdornment={<LockIcon />}
              endAdornment={
                <IconButton onClick={toggleShowPassword}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              }
              autoFocus={!!emailFromQuery}
            />

            <TextField
              {...register('firstname', {
                onChange: () => clearErrors('SignUpResult'),
              })}
              {...muiError(errors, 'firstname')}
              required
              fullWidth
              style={{ display: showContent ? 'none' : 'block' }}
              label="First name"
              autoComplete="Firstname"
              sx={{ pb: spc.between_vertical_s }}
              startAdornment={<PersonIcon />}
            />
            <TextField
              {...register('lastname', {
                onChange: () => clearErrors('SignUpResult'),
              })}
              {...muiError(errors, 'lastname')}
              required
              fullWidth
              style={{ display: showContent ? 'none' : 'block' }}
              label="Last name"
              autoComplete="lastname"
              sx={{ pb: spc.between_vertical_s }}
              startAdornment={<PersonIcon />}
            />
            <TextField
              {...register('job_title', {
                onChange: () => clearErrors('SignUpResult'),
              })}
              {...muiError(errors, 'job_title')}
              fullWidth
              required
              style={{ display: showContent ? 'none' : 'block' }}
              label="Your Job Title"
              autoComplete="Job Title"
              sx={{ pb: spc.between_vertical_s }}
              startAdornment={<WorkIcon />}
            />
            <Arranger
              direction="column"
              alignItems="flex-start"
              sx={{ pb: spc.between_vertical_xs }}
            >
              <Arranger direction="row" alignItems="center">
                <Checkbox {...register('termsAccepted')} sx={{ p: 0, mr: 3 }} />
                <Text sz="body12">
                  {'I agree to the '}
                  <Link href="/privacy" target="_blank">
                    Privacy Policy
                  </Link>
                  {' and '}
                  <Link href="/terms" target="_blank">
                    Terms of Use
                  </Link>
                  .
                </Text>
              </Arranger>
              {errors.termsAccepted && (
                <Text color="error" sz="body12" sx={{ mt: 1 }}>
                  {errors.termsAccepted.message}
                </Text>
              )}
            </Arranger>
            <input {...register('SignUpResult')} hidden />
            <ErrorMessage
              as={<Text color="error" />}
              errors={errors}
              name="SignUpResult"
            />
            <input {...register('SignUpSuccess')} hidden />

            <ErrorMessage
              as={<Text color="green" />}
              errors={errors}
              name="SignUpSuccess"
            />

            <Button
              loading={isLoading}
              type="submit"
              fullWidth
              disabled={!termsAccepted}
              sx={{ mt: spc.xs, mb: spc.between_vertical_s }}
            >
              Sign up
            </Button>
            <Arranger direction="column" alignItems="center">
              <Text sz="body12" sx={{ mb: spc.m }}>
                {'Already have an account? '}
                <Link href="/Sign-In">Sign In</Link>
              </Text>
            </Arranger>
          </form>
        </SmallForm>
      ) : (
        <SignUpSkeleton className={className} />
      )}
    </>
  );
};

export const SignUp = muiStyled(SignUpImpl)(
  ({ theme }) => css`
    .logo-container {
      margin-right: ${spc.l * 2}px;

      svg {
        width: 150px;
        height: 50px;
      }
    }
  `
);

export default SignUp;
